import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    OnInit
} from '@angular/core';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    DataService,
    NotificationService,
    GetAvailableCountriesQuery,
    OrderAddress,
} from '@vendure/admin-ui/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { Observable, shareReplay } from 'rxjs';
import { safeJSONParse } from '../utils';

@Component({
    selector: 'address-json-form',
    template: `
        <vdr-card>
            <div class="form-row">
                <div class="form-item">
                    <vdr-form-field label="Company (optional)">
                        <input type="text" [formControl]="companyControl" />
                    </vdr-form-field>
                </div>
            </div>
            <div class="form-row">
                <div class="form-item">
                    <vdr-form-field label="Address Line 1">
                        <input type="text" [formControl]="streetLine1Control" />
                    </vdr-form-field>
                </div>
                <div class="form-item">
                    <vdr-form-field label="Address Line 2 (optional)">
                        <input type="text" [formControl]="streetLine2Control" />
                    </vdr-form-field>
                </div>
            </div>

            <div class="form-row">
                <div class="form-item">
                    <vdr-form-field label="City">
                        <input type="text" [formControl]="cityControl" />
                    </vdr-form-field>
                </div>
                <div class="form-item">
                    <vdr-form-field label="State">
                        <input type="text" [formControl]="provinceControl" />
                    </vdr-form-field>
                </div>
            </div>

            <div class="form-row">
                <div class="form-item">
                    <vdr-form-field label="Zip Code">
                        <input type="text" [formControl]="postalCodeControl" />
                    </vdr-form-field>
                </div>
                <div *ngIf="availableCountries$ | async as countries" class="form-item">
                    <vdr-form-field label="Country">
                        <select [formControl]="countryCodeControl">
                            <option *ngFor="let c of countries" [value]="c.code">
                                {{ c.name }}
                            </option>
                        </select>
                    </vdr-form-field>
                </div>
            </div>
            <div class="form-row" *ngIf="!hidePhoneNumber">
                <div class="form-item">
                    <vdr-form-field label="Phone Number (optional)">
                        <input type="text" [formControl]="phoneNumberControl" />
                    </vdr-form-field>
                </div>
            </div>

        </vdr-card>
    `,
    styleUrls: ['../styles.scss'],
    standalone: true,
    imports: [SharedModule],
})
export class AddressFormInputComponent
    implements OnInit, AfterViewInit, FormInputComponent<StringCustomFieldConfig>
{
    readonly: boolean;
    hidePhoneNumber = false;

    private _config: StringCustomFieldConfig;
    get config(): StringCustomFieldConfig {
        return this._config;
    }
    set config(config: StringCustomFieldConfig) {
        this._config = config;
        this.hidePhoneNumber = !!(config as any)?.ui?.hidePhoneNumber;
    }
    formControl: FormControl;

    raw: any;
    companyControl = new UntypedFormControl();
    phoneNumberControl = new UntypedFormControl();
    streetLine1Control = new UntypedFormControl();
    streetLine2Control = new UntypedFormControl();
    cityControl = new UntypedFormControl();
    provinceControl = new UntypedFormControl();
    postalCodeControl = new UntypedFormControl();
    countryCodeControl = new UntypedFormControl();

    addressData: OrderAddress;

    availableCountries$: Observable<GetAvailableCountriesQuery['countries']['items']>;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private dataService: DataService,
        private notificationService: NotificationService) {}

    ngOnInit() {
        this.raw = this.formControl.value;
        this.availableCountries$ = this.dataService.settings
            .getAvailableCountries()
            .mapSingle(result => result.countries.items)
            .pipe(shareReplay(1));

        // Initialize once countries are loaded
        this.availableCountries$.subscribe(() => {
            this.applyRawValue(this.raw, /*emit*/ false);
            this.changeDetector.markForCheck();
        });

        // React to external changes (e.g. channel switch updating the parent formControl)
        this.formControl.valueChanges.subscribe(value => {
            this.raw = value;
            this.applyRawValue(this.raw, /*emit*/ false);
            this.changeDetector.markForCheck();
        });
    }

    ngAfterViewInit() {
        this.companyControl.valueChanges.subscribe(this.onCompanyChange);
        this.phoneNumberControl.valueChanges.subscribe(this.onPhoneNumberChange);
        this.streetLine1Control.valueChanges.subscribe(this.onStreetLine1Change);
        this.streetLine2Control.valueChanges.subscribe(this.onStreetLine2Change);
        this.cityControl.valueChanges.subscribe(this.onCityChange);
        this.provinceControl.valueChanges.subscribe(this.onProvinceChange);
        this.postalCodeControl.valueChanges.subscribe(this.onPostalCodeChange);
        this.countryCodeControl.valueChanges.subscribe(this.onCountryCodeChange);
    }

    private onCompanyChange = (value: string) => {
        if (this.addressData && this.addressData.company !== value) {
            this.addressData.company = value;
            this.updateJsonString();
        }
    }

    private onPhoneNumberChange = (value: string) => {
        if (this.addressData && this.addressData.phoneNumber !== value) {
            this.addressData.phoneNumber = value;
            this.updateJsonString();
        }
    }

    private onStreetLine1Change = (value: string) => {
        if (this.addressData && this.addressData.streetLine1 !== value) {
            this.addressData.streetLine1 = value;
            this.updateJsonString();
        }
    }

    private onStreetLine2Change = (value: string) => {
        if (this.addressData && this.addressData.streetLine2 !== value) {
            this.addressData.streetLine2 = value;
            this.updateJsonString();
        }
    }

    private onCityChange = (value: string) => {
        if (this.addressData && this.addressData.city !== value) {
            this.addressData.city = value;
            this.updateJsonString();
        }
    }

    private onProvinceChange = (value: string) => {
        if (this.addressData && this.addressData.province !== value) {
            this.addressData.province = value;
            this.updateJsonString();
        }
    }

    private onPostalCodeChange = (value: string) => {
        if (this.addressData && this.addressData.postalCode !== value) {
            this.addressData.postalCode = value;
            this.updateJsonString();
        }
    }

    private onCountryCodeChange = (value: string) => {
        if (this.addressData && this.addressData.countryCode !== value) {
            this.addressData.countryCode = value;
            this.updateJsonString();
        }
    }

    private updateJsonString() {
        if (!this.addressData) {
            return;
        }
        // Do not emit to avoid re-entering our own valueChanges subscription
        this.formControl.setValue(this.addressData, { emitEvent: false });
        this.formControl.markAsDirty();
    }

    /**
     * Apply a raw value (string or OrderAddress) to the internal controls.
     * When emit = false, control updates won't trigger valueChanges handlers.
     */
    private applyRawValue(raw: any, emit: boolean) {
        const defaultAddressData: OrderAddress = {
            company: '',
            phoneNumber: '',
            streetLine1: '',
            streetLine2: '',
            city: '',
            province: '',
            postalCode: '',
            countryCode: 'US',
        };

        if (typeof raw === 'string') {
            this.addressData =
                safeJSONParse<OrderAddress>(raw, this.notificationService, defaultAddressData)!;
        } else {
            this.addressData = { ...defaultAddressData, ...(raw as Partial<OrderAddress>) };
        }

        const opts = { emitEvent: emit };
        this.companyControl.setValue(this.addressData.company, opts);
        this.phoneNumberControl.setValue(this.addressData.phoneNumber, opts);
        this.streetLine1Control.setValue(this.addressData.streetLine1, opts);
        this.streetLine2Control.setValue(this.addressData.streetLine2, opts);
        this.cityControl.setValue(this.addressData.city, opts);
        this.provinceControl.setValue(this.addressData.province, opts);
        this.postalCodeControl.setValue(this.addressData.postalCode, opts);
        this.countryCodeControl.setValue(this.addressData.countryCode, opts);
    }
}
